import React, { useState } from 'react';
import { useToast } from '../../../../context/ToastContext';
import { useConfirm } from '../../../../context/ConfirmContext';
import { getAuthUrl, getPageInsights, testConnection } from '../api';

export default function FacebookSettings({ connection, onUpdate }) {
  const { addToast } = useToast();
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [insightsData, setInsightsData] = useState(null);
  const [settings, setSettings] = useState({
    name: connection.name
  });

  const handleReconnect = async () => {
    try {
      const redirectUri = `${window.location.origin}/connections/callback`;
      const authUrl = await getAuthUrl(redirectUri, connection.id);
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error starting reconnection:', error);
      addToast('Failed to start reconnection process', 'error');
    }
  };

  const handlePageSelect = async (pageId) => {
    const selectedPage = connection.settings?.availablePages?.find(
      p => p.id === pageId
    );
    if (!selectedPage) return;

    try {
      await onUpdate({
        ...connection,
        name: selectedPage.name,
        settings: {
          pageId: selectedPage.id,
          pageName: selectedPage.name,
          pageImageUrl: selectedPage.picture,
          pageAccessToken: selectedPage.accessToken,
          is_configured: true
        }
      });
      addToast('Page selected successfully', 'success');
    } catch (error) {
      addToast('Failed to update page selection', 'error');
    }
  };

  const handleTestConnection = async () => {
    setTestLoading(true);
    try {
      // Test basic connection
      const result = await testConnection({
        accessToken: connection.credentials.accessToken,
        pageId: connection.settings?.pageId,
        pageAccessToken: connection.settings?.pageAccessToken
      });

      if (!result.success) {
        throw new Error(result.error || 'Connection test failed');
      }

      // Get insights data if available
      if (connection.settings?.pageId) {
        const insights = await getPageInsights(
          connection.settings.pageId,
          connection.settings.pageAccessToken
        );
        setInsightsData(insights);
      }

      addToast('Connection test successful', 'success');
    } catch (error) {
      console.error('Test connection error:', error);
      addToast('Connection test failed: ' + error.message, 'error');
    } finally {
      setTestLoading(false);
    }
  };

  const handleResetConnection = async () => {
    const confirmed = await confirm({
      title: 'Reset Connection',
      message: 'Are you sure you want to reset this connection? You will need to reconnect with Facebook.',
      confirmText: 'Reset Connection',
      confirmVariant: 'danger'
    });

    if (confirmed) {
      try {
        await onUpdate({
          ...connection,
          credentials: null,
          settings: {
            name: connection.name
          }
        });
        addToast('Connection reset successfully', 'success');
      } catch (error) {
        addToast('Failed to reset connection', 'error');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onUpdate({
        ...connection,
        name: settings.name.trim(),
        updated_at: new Date().toISOString()
      });

      addToast('Settings updated successfully', 'success');
    } catch (error) {
      console.error('Error updating settings:', error);
      addToast('Failed to update settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  // If no credentials, show reconnect screen
  if (!connection.credentials?.accessToken) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">Facebook Connection</h3>
        </div>

        <div className="bg-dark-300 p-6 rounded-lg text-center space-y-4">
          <p className="text-gray-400">
            This connection needs to be reconnected to Facebook.
          </p>
          <button
            onClick={handleReconnect}
            className="px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90"
          >
            Reconnect to Facebook
          </button>
        </div>
      </div>
    );
  }

  // Show page selection if no page is selected but pages are available
  if (connection.credentials?.accessToken && !connection.settings?.pageId && connection.settings?.availablePages?.length > 0) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium">Select a Facebook Page</h3>
        <div className="grid gap-4">
          {connection.settings.availablePages.map(page => (
            <button
              key={page.id}
              onClick={() => handlePageSelect(page)}
              className="flex items-center p-4 border border-divider rounded-lg hover:border-accent-primary transition-colors text-left"
            >
              {page.picture && (
                <img
                  src={page.picture}
                  alt={page.name || 'Facebook Page'}
                  className="w-12 h-12 rounded-full object-cover"
                />
              )}
              <div className="ml-4">
                <div className="font-medium">{page.name || 'Facebook Page'}</div>
                {page.category && (
                  <div className="text-sm text-gray-400">{page.category}</div>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  {(page.followersCount || 0).toLocaleString()} followers · {(page.likesCount || 0).toLocaleString()} likes
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Show selected page info
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Facebook Connection</h3>
        <button
          type="button"
          onClick={handleResetConnection}
          className="px-3 py-1 text-sm text-red-500 hover:text-red-400 transition-colors"
        >
          Reset Connection
        </button>
      </div>

      {connection.settings?.pageId && (
        <>
          <div className="bg-dark-300 p-4 rounded-lg">
            <div className="flex items-center space-x-4">
              {connection.settings.pageImageUrl && (
                <img
                  src={connection.settings.pageImageUrl}
                  alt={connection.settings.pageName || 'Facebook Page'}
                  className="w-12 h-12 rounded-full object-cover"
                />
              )}
              <div>
                <div className="font-medium">
                  {connection.settings.pageName || 'Facebook Page'}
                </div>
                {connection.settings.pageCategory && (
                  <div className="text-sm text-gray-400">
                    {connection.settings.pageCategory}
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  {(connection.settings.followersCount || 0).toLocaleString()} followers · {(connection.settings.likesCount || 0).toLocaleString()} likes
                </div>
              </div>
            </div>
          </div>

          {/* Test Connection Button */}
          <div className="pt-4">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testLoading}
              className="w-full px-4 py-2 bg-accent-secondary text-white rounded hover:bg-accent-secondary/90 disabled:opacity-50 transition-colors"
            >
              {testLoading ? 'Testing Connection...' : 'Test Connection'}
            </button>
          </div>

          {/* Insights Data */}
          {insightsData && (
            <div className="space-y-4">
              <h4 className="font-medium">Page Insights:</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-dark-400 p-4 rounded">
                  <div className="text-sm text-gray-400">Current Followers</div>
                  <div className="text-2xl font-semibold">{(insightsData.currentFollowers || 0).toLocaleString()}</div>
                </div>
                <div className="bg-dark-400 p-4 rounded">
                  <div className="text-sm text-gray-400">Current Page Likes</div>
                  <div className="text-2xl font-semibold">{(insightsData.currentLikes || 0).toLocaleString()}</div>
                </div>
                <div className="bg-dark-400 p-4 rounded">
                  <div className="text-sm text-gray-400">People Talking</div>
                  <div className="text-2xl font-semibold">{(insightsData.peopleTalking || 0).toLocaleString()}</div>
                </div>
                <div className="bg-dark-400 p-4 rounded">
                  <div className="text-sm text-gray-400">Engaged Users</div>
                  <div className="text-2xl font-semibold">{(insightsData.engagedUsers || 0).toLocaleString()}</div>
                </div>
                <div className="bg-dark-400 p-4 rounded">
                  <div className="text-sm text-gray-400">Post Engagements</div>
                  <div className="text-2xl font-semibold">{(insightsData.postEngagements || 0).toLocaleString()}</div>
                </div>
                <div className="bg-dark-400 p-4 rounded">
                  <div className="text-sm text-gray-400">Online Fans</div>
                  <div className="text-2xl font-semibold">{(insightsData.onlineFans || 0).toLocaleString()}</div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Connection Name Form */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Connection Name
        </label>
        <input
          type="text"
          value={settings.name}
          onChange={(e) => setSettings({ ...settings, name: e.target.value })}
          className="w-full px-3 py-2 bg-dark-300 border border-divider rounded focus:outline-none focus:border-accent-primary"
          required
        />
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          className="px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 disabled:opacity-50"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
} 